import { IfcWriter } from './ifc-writer';
import { IfcEntity, IfcExportOptions, IfcPoint, IfcWallEntity } from './types';

const DEFAULT_WALL_THICKNESS = 0.01;

/**
 * Shift every coordinate of an entity by `-origin`, so the written geometry uses
 * small, local numbers. The origin is re-applied via the IfcSite placement.
 */
function offsetEntity(entity: IfcEntity, o: IfcPoint): IfcEntity {
  switch (entity.type) {
    case 'WALL':
      return {
        ...entity,
        startPoint: { x: entity.startPoint.x - o.x, y: entity.startPoint.y - o.y, z: entity.startPoint.z - o.z },
        endPoint: { x: entity.endPoint.x - o.x, y: entity.endPoint.y - o.y, z: entity.endPoint.z - o.z },
        openings: entity.openings?.map((op) => ({
          ...op,
          footprint: op.footprint.map((p) => ({ x: p.x - o.x, y: p.y - o.y })),
          elevation: op.elevation - o.z,
        })),
        bodyFootprint: entity.bodyFootprint?.map((p) => ({ x: p.x - o.x, y: p.y - o.y })),
      };
    case 'WALL_OUTLINE':
      return {
        ...entity,
        outerLoop: entity.outerLoop.map((p) => ({ x: p.x - o.x, y: p.y - o.y })),
        innerLoops: entity.innerLoops?.map((loop) => loop.map((p) => ({ x: p.x - o.x, y: p.y - o.y }))),
        elevation: entity.elevation - o.z,
        openings: entity.openings?.map((op) => ({
          ...op,
          footprint: op.footprint.map((p) => ({ x: p.x - o.x, y: p.y - o.y })),
          elevation: op.elevation - o.z,
        })),
      };
    case 'SPACE':
      return {
        ...entity,
        footprint: entity.footprint.map((p) => ({ x: p.x - o.x, y: p.y - o.y })),
        elevation: entity.elevation - o.z,
      };
  }
}

/**
 * Compute the 4 quad points for a wall cross-section.
 * The wall is a thin rectangle perpendicular to the wall edge direction,
 * centered on the edge line.
 *
 * Returns 4 points in 2D (XY plane) forming the wall footprint.
 */
function computeWallQuad(wall: IfcWallEntity, defaultThickness: number): Array<{ x: number; y: number }> {
  const thickness = wall.thickness ?? defaultThickness;
  const halfThickness = thickness / 2;

  const dx = wall.endPoint.x - wall.startPoint.x;
  const dy = wall.endPoint.y - wall.startPoint.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length < 1e-10) {
    // Degenerate wall (zero length), return a tiny square
    return [
      { x: wall.startPoint.x - halfThickness, y: wall.startPoint.y - halfThickness },
      { x: wall.startPoint.x + halfThickness, y: wall.startPoint.y - halfThickness },
      { x: wall.startPoint.x + halfThickness, y: wall.startPoint.y + halfThickness },
      { x: wall.startPoint.x - halfThickness, y: wall.startPoint.y + halfThickness },
    ];
  }

  // Normal perpendicular to wall direction
  const nx = -dy / length;
  const ny = dx / length;

  const offsetX = nx * halfThickness;
  const offsetY = ny * halfThickness;

  // Quad corners: start-left, start-right, end-right, end-left
  return [
    { x: wall.startPoint.x + offsetX, y: wall.startPoint.y + offsetY },
    { x: wall.startPoint.x - offsetX, y: wall.startPoint.y - offsetY },
    { x: wall.endPoint.x - offsetX, y: wall.endPoint.y - offsetY },
    { x: wall.endPoint.x + offsetX, y: wall.endPoint.y + offsetY },
  ];
}

/**
 * Export an array of IFC entities to an IFC2X3 STEP string.
 *
 * Follows the same structure as the C++ export_IFC.cpp:
 * - Header with IFC2X3 schema
 * - Project hierarchy (IfcProject -> IfcBuilding -> IfcBuildingStorey)
 * - Wall entities with extruded quad profiles
 * - Spatial containment linking walls to storey
 */
export function exportIfc(entitiesIn: IfcEntity[], options?: IfcExportOptions): string {
  const projectName = options?.projectName ?? 'Ifc Project';
  const defaultThickness = options?.defaultWallThickness ?? DEFAULT_WALL_THICKNESS;

  // Local origin (e.g. point-cloud bbox min) keeps coordinates small and positive.
  // Geometry is shifted by -origin; the IfcSite placement re-applies it.
  const origin = options?.origin;
  const entities = origin ? entitiesIn.map((e) => offsetEntity(e, origin)) : entitiesIn;

  const writer = new IfcWriter();

  // HEADER
  writer.writeHeader('walls_export.ifc');

  // Project hierarchy (project + site + building; storeys are written lazily below)
  const refs = writer.writeProjectHierarchy(projectName, options?.site);

  // Record the subtracted offset (+ CRS) on the IfcSite so the georeference stays
  // recoverable as `world = origin + local` without moving geometry from the origin.
  if (origin) writer.writeSiteGeoreferencing(refs.site, origin, options?.site?.crsName);

  // Storeys are emitted on first use, so only floors that contain something appear.
  // Entities with no/unknown storeyId share a single "Default Storey".
  const DEFAULT_KEY = '__default__';
  const storeyMeta = new Map<string, { name: string; elevation: number }>();
  for (const s of options?.storeys ?? []) {
    storeyMeta.set(s.id, { name: s.name ?? 'Storey', elevation: (s.elevation ?? 0) - (origin?.z ?? 0) });
  }
  const storeyRefByKey = new Map<string, string>();
  const getStorey = (id?: string): string => {
    const key = id && storeyMeta.has(id) ? id : DEFAULT_KEY;
    let ref = storeyRefByKey.get(key);
    if (!ref) {
      const meta = storeyMeta.get(key) ?? { name: 'Default Storey', elevation: 0 };
      ref = writer.writeStorey(meta.name, meta.elevation, refs.building);
      storeyRefByKey.set(key, ref);
    }
    return ref;
  };

  // Write wall entities, accumulating their refs per containing storey.
  const wallRefsByStorey = new Map<string, string[]>();
  const contain = (storeyRef: string, ...refsToAdd: string[]) => {
    const list = wallRefsByStorey.get(storeyRef) ?? [];
    list.push(...refsToAdd);
    wallRefsByStorey.set(storeyRef, list);
  };
  let wallIndex = 0;
  // Wall refs by their stable id, so options.connections can wire up joins.
  const wallRefById = new Map<string, string>();
  // Space refs by room id (for space boundaries) and per storey (for aggregation).
  const spaceRefById = new Map<string, string>();
  const spaceRefsByStorey = new Map<string, string[]>();
  let spaceIndex = 0;

  for (const entity of entities) {
    const storeyRef = getStorey(entity.storeyId);
    switch (entity.type) {
      case 'SPACE': {
        // Unique per-export number (Revit room Number); the display name rides on LongName.
        const space = writer.writeSpace(`${spaceIndex + 1}`, entity.name ?? `space_${spaceIndex}`, entity.footprint, entity.elevation, entity.height, refs);
        const list = spaceRefsByStorey.get(storeyRef) ?? [];
        list.push(space);
        spaceRefsByStorey.set(storeyRef, list);
        if (entity.id) spaceRefById.set(entity.id, space);
        spaceIndex++;
        break;
      }
      case 'WALL': {
        // Explicit body profile (curved walls: arc band) wins over the centered quad.
        const quad = entity.bodyFootprint && entity.bodyFootprint.length >= 3 ? entity.bodyFootprint : computeWallQuad(entity, defaultThickness);
        const elevation = Math.min(entity.startPoint.z, entity.endPoint.z);
        const thickness = entity.thickness ?? defaultThickness;
        const { wall, fillings } = writer.writeWallStandardCase(
          entity.name ?? `wall_${wallIndex}`,
          quad,
          { x: entity.startPoint.x, y: entity.startPoint.y },
          { x: entity.endPoint.x, y: entity.endPoint.y },
          elevation,
          entity.height,
          thickness,
          refs,
          entity.openings,
        );
        contain(storeyRef, wall, ...fillings);
        if (entity.id) wallRefById.set(entity.id, wall);
        wallIndex++;
        break;
      }
      case 'WALL_OUTLINE': {
        const { wall, fillings } = writer.writeWallFromOutline(entity.name ?? `wall_outline_${wallIndex}`, entity.outerLoop, entity.innerLoops, entity.elevation, entity.height, refs, entity.openings);
        // Wall and its door/window fillings are all contained in the same storey.
        contain(storeyRef, wall, ...fillings);
        wallIndex++;
        break;
      }
    }
  }

  // Guarantee at least one storey so a geometry-less file stays valid.
  if (storeyRefByKey.size === 0) getStorey();

  // Spatial containment: one relationship per storey that actually has walls.
  for (const [storeyRef, refsList] of wallRefsByStorey) {
    writer.writeSpatialContainment(refsList, storeyRef);
  }

  // Spatial decomposition: aggregate each storey's spaces under it.
  for (const [storeyRef, spaceList] of spaceRefsByStorey) {
    writer.writeSpaceAggregation(storeyRef, spaceList);
  }

  // Room↔wall bonds. Skip any referencing a space or wall that wasn't written.
  for (const b of options?.spaceBoundaries ?? []) {
    const space = spaceRefById.get(b.spaceId);
    const wall = wallRefById.get(b.wallId);
    if (space && wall) writer.writeSpaceBoundary(space, wall);
  }

  // Footer
  writer.writeFooter();

  return writer.toString();
}
